/**
 * Zeitraum des Meilenstein-Zeitbands — welcher Ausschnitt gezeigt wird und wo
 * ein Datum darin liegt.
 *
 * **Warum das Fenster nicht mehr aus der Anlage der Meilensteine kommt.** Das
 * Band spannte bisher von der frühesten Anlage bis zur spätesten Frist. Damit
 * begann jeder Balken an dem Tag, an dem jemand den Meilenstein angelegt hat —
 * eine Verwaltungsspur, kein Plan. Ein im Januar angelegter Meilenstein mit
 * Aufgaben im Oktober füllte das halbe Jahr davor mit Balken, in dem niemand
 * etwas vorhatte. Der Ausschnitt folgt deshalb den **Fristen der aufgeplanten
 * Aufgaben**; wo keine stehen, bleibt der Meilenstein mit seiner eigenen Frist
 * das einzige Datum.
 *
 * **Warum ganze Monate.** Die Achse zeigt Monatsnamen in gleich breiten
 * Zellen. Ein Fenster, das mitten im März beginnt, macht diese Zellen zu einer
 * Behauptung: Der erste April läge dann bei 24 % statt bei 33 %, und der
 * Balken stünde sichtbar unter dem falschen Monat. Fenster und Achse rechnen
 * hier deshalb beide in **Monatsbreiten** — ein Monat ist eine Zelle, der Tag
 * darin sein Anteil. Die Zelle ist damit genau so lang, wie sie aussieht,
 * gleich ob Februar oder März.
 *
 * Ohne Datenbank: `apps/web` bestimmt den Ausschnitt hiermit, ohne dafür eine
 * Serverprozedur zu brauchen.
 */
/** Registry-Schlüssel der Projekteinstellung (Bereich `projekt`). */
export const ZEITSTRAHL_ZEITRAUM_SCHLUESSEL = "zeitstrahl_zeitraum";
export const ZEITSTRAHL_ZEITRAEUME = [
    "automatisch",
    "1_monat",
    "3_monate",
    "6_monate",
    "12_monate",
];
/** Beschriftung der Auswahl in den Projekteinstellungen. */
export const ZEITSTRAHL_ZEITRAUM_LABEL = {
    automatisch: "Automatisch — folgt den Aufgaben",
    "1_monat": "1 Monat",
    "3_monate": "3 Monate",
    "6_monate": "6 Monate",
    "12_monate": "12 Monate",
};
/** Wie viele Monate eine feste Spanne umfasst; `null` heißt automatisch. */
const MONATE_JE_ZEITRAUM = {
    automatisch: null,
    "1_monat": 1,
    "3_monate": 3,
    "6_monate": 6,
    "12_monate": 12,
};
export function istZeitstrahlZeitraum(wert) {
    return (typeof wert === "string" &&
        ZEITSTRAHL_ZEITRAEUME.includes(wert));
}
function monatsIndex(wert) {
    return wert.getFullYear() * 12 + wert.getMonth();
}
function monatsAnfang(wert) {
    return new Date(wert.getFullYear(), wert.getMonth(), 1);
}
function monateSpaeter(wert, anzahl) {
    return new Date(wert.getFullYear(), wert.getMonth() + anzahl, 1);
}
/** Anteil eines Datums an seinem eigenen Monat — 0 am Ersten, knapp 1 am Letzten. */
function tagesAnteil(wert) {
    const anfang = monatsAnfang(wert).getTime();
    const naechster = monateSpaeter(wert, 1).getTime();
    return (wert.getTime() - anfang) / (naechster - anfang);
}
/**
 * Der Ausschnitt für einen Zeitraum.
 *
 * Feste Spanne: ab dem laufenden Monat so viele Monate, wie der Zeitraum
 * nennt. Automatisch: vom frühesten bis zum spätesten Termin, aufgerundet auf
 * ganze Monate — und **heute liegt immer darin**. Ohne diese Klammer klebte
 * die Heute-Linie an einer Bandkante und behauptete einen Stand, den das Band
 * gar nicht zeigt.
 *
 * `null` heißt: automatisch, aber es gibt keinen einzigen Termin — dann ist
 * kein Band zu zeichnen, das etwas aussagt.
 */
export function berechneZeitstrahlFenster(zeitraum, termine, jetzt) {
    const monate = MONATE_JE_ZEITRAUM[zeitraum];
    if (monate !== null) {
        const von = monatsAnfang(jetzt);
        return { von, bis: monateSpaeter(von, monate) };
    }
    const gueltig = termine.filter((t) => Number.isFinite(t.getTime()));
    if (gueltig.length === 0)
        return null;
    let frueh = jetzt.getTime();
    let spaet = jetzt.getTime();
    for (const t of gueltig) {
        const zeit = t.getTime();
        if (zeit < frueh)
            frueh = zeit;
        if (zeit > spaet)
            spaet = zeit;
    }
    return {
        von: monatsAnfang(new Date(frueh)),
        bis: monateSpaeter(new Date(spaet), 1),
    };
}
/** Anzahl der Monatszellen im Fenster. */
export function monateImFenster(fenster) {
    return Math.max(monatsIndex(fenster.bis) - monatsIndex(fenster.von), 0);
}
/** Lage eines Datums im Fenster als Anteil 0–1, in Monatsbreiten gerechnet. */
export function anteilImFenster(wert, fenster) {
    const gesamt = monateImFenster(fenster);
    if (gesamt <= 0)
        return 0;
    const roh = monatsIndex(wert) - monatsIndex(fenster.von) + tagesAnteil(wert);
    return Math.min(Math.max(roh / gesamt, 0), 1);
}
/** Dieselbe Lage als CSS-Prozentwert, z. B. `"47.7%"`. */
export function positionImFenster(wert, fenster) {
    return `${(anteilImFenster(wert, fenster) * 100).toFixed(1)}%`;
}
/**
 * Breite einer Spanne in Prozent der Bandbreite — nicht die rechte Kante:
 * Sobald die Spanne links aus dem Fenster ragt, liegt ihre linke Kante bei
 * 0 %, und eine als „Breite" durchgereichte Kantenposition liefe über das
 * Band hinaus.
 */
export function spanneImFenster(von, bis, fenster) {
    const breite = Math.max(anteilImFenster(bis, fenster) - anteilImFenster(von, fenster), 0);
    return `${(breite * 100).toFixed(1)}%`;
}
/** Die Monatszellen des Fensters, von links nach rechts. */
export function monatsAchse(fenster) {
    const achse = [];
    const anzahl = monateImFenster(fenster);
    for (let i = 0; i < anzahl; i += 1) {
        const zelle = monateSpaeter(fenster.von, i);
        achse.push({ jahr: zelle.getFullYear(), monat: zelle.getMonth() });
    }
    return achse;
}
//# sourceMappingURL=zeitstrahl.js.map